/**
 * Authentication helpers.
 *
 * Auth is carried by HttpOnly access/refresh cookies set by the backend.
 * Axios is configured globally with withCredentials=true in App.js, so callers
 * don't need to attach an Authorization header.
 *
 * The localStorage entry below is a UI-only signal (used by RequireAuth and
 * HeaderNav). Server still enforces authentication via the cookie.
 */

import axios from 'axios';

const AUTH_USER_KEY = 'lssi-auth-user';
const API_BASE = 'https://api.lssi-fin.com';

/**
 * Get headers for API requests. Kept as a function so existing call sites
 * (axios.get(url, { headers: getAuthHeaders() })) continue to work after the
 * cookie-JWT migration; the cookie carries auth, so this only sets Content-Type.
 */
export function getAuthHeaders() {
  return { 'Content-Type': 'application/json' };
}

export function getAuthHeader() {
  // Legacy helper — no Authorization header needed with cookie auth.
  return '';
}

export function isAuthenticated() {
  return !!localStorage.getItem(AUTH_USER_KEY);
}

export function getCurrentUser() {
  try {
    const raw = localStorage.getItem(AUTH_USER_KEY);
    if (!raw) return null;
    const u = JSON.parse(raw);
    return u?.username || u?.email || null;
  } catch {
    return null;
  }
}

export function getCurrentUserObject() {
  try {
    const raw = localStorage.getItem(AUTH_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setCurrentUser(user) {
  if (user) {
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(AUTH_USER_KEY);
  }
}

/**
 * POST to the backend logout endpoint so the refresh token gets blacklisted,
 * then clear the localStorage marker. Cookies are cleared by the server.
 */
export async function logout() {
  try {
    await axios.post(`${API_BASE}/api/auth/logout/`);
  } catch {
    // Ignore — clear local state regardless so the user lands on /login.
  }
  localStorage.removeItem(AUTH_USER_KEY);
}

// Dedup concurrent refresh calls. Backend rotates+blacklists the refresh token
// on every use, so two parallel POSTs to /api/auth/refresh/ would race: the
// second one finds its refresh token already rotated and 401s, killing the
// session. Sharing one in-flight promise keeps the 401 interceptor, visibility
// listener, interval, and boot sequence safe to fire near-simultaneously.
let inFlightRefresh = null;

export function refreshTokens() {
  if (inFlightRefresh) return inFlightRefresh;
  inFlightRefresh = axios
    .post(`${API_BASE}/api/auth/refresh/`, null, { withCredentials: true })
    .finally(() => { inFlightRefresh = null; });
  return inFlightRefresh;
}
